/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * Attribute Mapping Service
 * Single responsibility: Handle AttributeMapping logic for any step
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type {
  AttributeMappingConfig,
  GenericAttributeMapping,
} from '../../../../components/AttributeMapping';
import type { RootState } from '../../../../services/serviceTypes';
import { useFetchAttributesDataQuery } from '../../data-store/copilotApi';
import {
  setSavedStepData,
  updateStepFormData,
} from '../../data-store/copilotSlice';
import { useApplicationName } from '../../hooks/index';

interface UseAttributeMappingLogicProps {
  stepName: string;
}

/**
 * Reusable attribute mapping logic hook
 * Provides AttributeMapping functionality for any step
 */
export const useAttributeMappingLogic = ({
  stepName,
}: UseAttributeMappingLogicProps) => {
  const dispatch = useDispatch();
  const [mappings, setMappings] = useState<GenericAttributeMapping[]>([]);
  const applicationName: string = useApplicationName();
  const isFirstLoad = useRef(true);

  // Get step template data from Redux
  const parsedTemplates = useSelector(
    (state: RootState) => state.copilot.parsedTemplates
  );

  const currentMode = useSelector((state: RootState) => state.copilot.ui.mode);

  const stepData = parsedTemplates[stepName];

  // Fetch attributes data using RTK Query
  const queryResult = useFetchAttributesDataQuery(
    {
      apiURL: stepData.apiURL ?? '',
      domainObjects: stepData.domainObjects ?? [],
    },
    {
      skip: !stepData.apiURL || !stepData.domainObjects,
    }
  );

  const attributesData = queryResult.data as unknown;
  const isAttributesLoading = queryResult.isLoading;

  const mandatoryMappings: GenericAttributeMapping[] = useMemo(
    () => [
      {
        id: 'mandatory-1',
        saviyntField: '',
        externalAppField: '',
        saviyntValue: '',
        externalAppValue: '',
        isMandatory: true,
      },
    ],
    []
  );

  // Get saved form data from Redux
  const stepFormData = useSelector(
    (state: RootState) => state.copilot.stepFormData
  );

  const initialMappings: GenericAttributeMapping[] = useMemo(() => {
    // Get saved mappings for this step only
    const currentStepData = stepFormData[stepName] || {};
    const createAccountData = stepFormData.CreateAccount || {};

    let savedMappings;

    if (currentStepData.mappings && Array.isArray(currentStepData.mappings)) {
      savedMappings = currentStepData.mappings;
    } else if (
      stepName === 'UpdateAccount' &&
      createAccountData.mappings &&
      Array.isArray(createAccountData.mappings)
    ) {
      // Only UpdateAccount can fallback to CreateAccount mappings
      savedMappings = createAccountData.mappings;
    }
    // Enable, Disable, Remove steps start empty - no fallback

    const filledMappings =
      savedMappings && Array.isArray(savedMappings)
        ? savedMappings
            .filter(
              (mapping: any) => mapping.saviyntField && mapping.externalAppField
            )
            .map((mapping: any, index: number) => ({
              id: `from-redux-${index}`,
              saviyntField: mapping.saviyntField,
              externalAppField: mapping.externalAppField,
              saviyntValue: '',
              externalAppValue: '',
              isMandatory: false,
            }))
        : [];

    // Only add mandatory mappings if no existing data
    return filledMappings.length > 0
      ? filledMappings
      : [...filledMappings, ...mandatoryMappings];
  }, [stepFormData, stepName, mandatoryMappings]);

  // Create dynamic config from API data
  const dynamicConfig: AttributeMappingConfig = useMemo(() => {
    if (
      !attributesData ||
      typeof attributesData !== 'object' ||
      !('tableArributes' in attributesData) ||
      typeof (attributesData as { tableArributes?: unknown }).tableArributes !==
        'object'
    ) {
      return {
        saviyntApp: {
          name: 'Saviynt',
          icon: 'SaviyntLogoFilledSeparateColors',
          fields: [],
          allowCustomValues: false,
        },
        externalApp: {
          name: applicationName,
          icon: applicationName,
          fields: [],
          allowCustomValues: true,
        },
      };
    }

    const tableAttributes = (
      attributesData as { tableArributes: Record<string, unknown> }
    ).tableArributes;
    const flattenedAttributes = Object.entries(tableAttributes)
      .flatMap(([parentKey, attributes]) => {
        if (Array.isArray(attributes)) {
          return attributes.map(
            (attr: unknown) =>
              `${parentKey}.${String((attr as { name: unknown }).name)}`
          );
        }

        return [];
      })
      .concat(['password.'])
      .sort((a, b) => {
        const priorityOrder = [
          'usersObj',
          'arstasksObj',
          'usersManagerObj',
          'endpointsObj',
          'entitlementValuesObj',
          'password',
        ];

        const getPriority = (str: string) => {
          const index = priorityOrder.findIndex((prefix) =>
            str.startsWith(prefix)
          );

          return index === -1 ? priorityOrder.length : index;
        };

        const priorityA = getPriority(a);
        const priorityB = getPriority(b);

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        const getAttributeLength = (str: string) => {
          const parts = str.split('.');

          return parts.length > 1 ? parts[1].length : str.length;
        };

        return getAttributeLength(a) - getAttributeLength(b);
      });

    return {
      saviyntApp: {
        name: 'Saviynt',
        icon: 'SaviyntLogoFilledSeparateColors',
        fields: flattenedAttributes,
        allowCustomValues: false,
      },
      externalApp: {
        name: applicationName,
        icon: applicationName,
        fields: [],
        allowCustomValues: true,
      },
    };
  }, [applicationName, attributesData]);

  // Load initial mappings into component state with proper ordering
  useEffect(() => {
    if (initialMappings.length > 0) {
      // Sort mappings: filled ones first, then empty ones
      const sortedMappings = [...initialMappings].sort((a, b) => {
        const aFilled = !!(a.saviyntField && a.externalAppField);
        const bFilled = !!(b.saviyntField && b.externalAppField);

        if (aFilled && !bFilled) {
          return -1;
        } // a comes first

        if (!aFilled && bFilled) {
          return 1;
        } // b comes first

        return 0; // maintain original order for same type
      });

      setMappings(sortedMappings);
    }
  }, [initialMappings]);

  const handleMappingsChange = useCallback(
    (newMappings: GenericAttributeMapping[]) => {
      // Sort mappings: filled ones first, then empty ones
      const sortedMappings = [...newMappings].sort((a, b) => {
        const aFilled = !!(a.saviyntField && a.externalAppField);
        const bFilled = !!(b.saviyntField && b.externalAppField);

        if (aFilled && !bFilled) {
          return -1;
        } // a comes first

        if (!aFilled && bFilled) {
          return 1;
        } // b comes first

        return 0; // maintain original order for same type
      });

      setMappings(sortedMappings);

      if (sortedMappings.length > 0) {
        // Create mapping object for template replacement
        const mappingObject = sortedMappings.reduce<Record<string, string>>(
          (acc, mapping) => {
            if (mapping.saviyntField && mapping.externalAppField) {
              acc[String(mapping.externalAppField)] = `\${${String(
                mapping.saviyntField
              )}}`;
            }

            return acc;
          },
          {}
        );

        // Get template from stepData
        const template =
          (stepData as { attributeDefaultValue?: string })
            .attributeDefaultValue ?? '';

        // Replace @MAPPINGJSON@ with actual mappings
        const mappingJson = JSON.stringify(mappingObject)
          .slice(1, -1)
          .replace(/"/g, '\\"');

        const transformedTemplate = template.replace(
          '@MAPPINGJSON@',
          mappingJson
        );

        // Create final payload with correct JSON key based on step
        const jsonKey = `externalconnattvalue.${stepName}JSON`;
        const payload = {
          multipleconnection: false,
          [jsonKey]: transformedTemplate,
          mappings: sortedMappings.map((mapping) => ({
            saviyntField: mapping.saviyntField,
            externalAppField: mapping.externalAppField,
          })), // Store clean mappings for validation
        };

        // Persist to Redux
        dispatch(
          updateStepFormData({
            stepName,
            formData: payload,
          })
        );

        if (currentMode === 'edit' && isFirstLoad.current) {
          isFirstLoad.current = false;
          dispatch(
            setSavedStepData({
              stepName,
              formData: payload,
            })
          );
        }
      }
    },
    [stepData, stepName, dispatch, currentMode]
  );

  return {
    // Data
    mappings,
    dynamicConfig,
    initialMappings,
    mandatoryMappings: [], // Empty since they're included in initialMappings
    attributesData,

    // Loading states
    isAttributesLoading,

    // Handlers
    handleMappingsChange,
  };
};

export default useAttributeMappingLogic;
